/**
 * GamepadManager - Handles gamepad slot assignment, XInput conversion, and button filtering.
 *
 * Owns per-peer gamepad slot mappings (coordinator-owned state). Reads host-level
 * gamepad configuration (autoAssign, maxAssignments, shareFlags) and input rules
 * from injected coordinator-owned state stores. Converts incoming gamepad setState
 * messages to XInput format and sends directly to IPC via IIpcService.
 */

import type { PlaycastGamepadId, PlaycastGamepadXInput, StateStore, XInput } from '@playcastdotio/CoreTypes';
import type { PlaycastMessage, PlaycastMessageGamepadSetState } from '@playcastdotio/Messaging';
import { generators } from '@playcastdotio/Messaging';
import type { IIpcService } from '../../../services/ipc/IIpcService';
import type { AppCoordinator } from '../../app/AppCoordinator';
import type { GamepadConfigState, InputRulesState } from '../stores/inputConfigStores';

export const MAX_GAMEPAD_SLOTS = 4;

const BUTTONS_TO_FILTER = [0x0010, 0x0020]; // START and BACK
const FILTER_MASK = BUTTONS_TO_FILTER.reduce((acc, b) => acc | b, 0);

export type GamepadManagerDeps = {
  ipcService: IIpcService;
  gamepadConfigStore: StateStore<GamepadConfigState>;
  inputRulesStore: StateStore<InputRulesState>;
  appCoordinator: AppCoordinator;
};

export class GamepadManager {
  private slotAssignments = new Map<string, PlaycastGamepadId[]>();

  constructor(private readonly deps: GamepadManagerDeps) {}

  public handleGamepadMessage(peerId: string, message: PlaycastMessage<PlaycastMessageGamepadSetState>): void {
    const gamepads = message.body.message.gamepads;
    const playerCoordinates = message.body.message.playerCoordinates;
    const hostId = this.deps.appCoordinator.getHostAuthId();
    const userAuth = message.header.user?.auth ?? '';

    for (let i = 0; i < gamepads.length; i++) {
      const device = gamepads[i];
      const slotIndex = this.resolveSlot(peerId, i);
      if (slotIndex === -1) continue;

      const xInput = this.applyButtonFiltering(device.xInput);
      const xinputPayload: PlaycastGamepadXInput = {
        gamepadId: slotIndex as PlaycastGamepadId,
        playerCoordinates,
        xInput,
      };

      const ipcMessage = generators.gamepad.setXInput(hostId, xinputPayload, userAuth);
      this.deps.ipcService.send(ipcMessage);
    }
  }

  public removePeer(peerId: string): void {
    this.slotAssignments.delete(peerId);
  }

  public getSlotAssignments(peerId: string): PlaycastGamepadId[] {
    return this.slotAssignments.get(peerId) ?? [];
  }

  /**
   * Manually assign a specific slot to a peer.
   * Evicts the slot from other peers if not shared.
   */
  public assignSlot(peerId: string, slotIndex: PlaycastGamepadId): void {
    const peerSlots = this.slotAssignments.get(peerId) ?? [];
    if (peerSlots.includes(slotIndex)) return;

    const config = this.deps.gamepadConfigStore.currentState;
    if (!config.shareFlags[slotIndex]) {
      this.removeSlotFromOtherPeers(slotIndex, peerId);
    }

    peerSlots.push(slotIndex);
    this.slotAssignments.set(peerId, peerSlots);
  }

  /**
   * Manually remove a specific slot from a peer.
   */
  public removeSlot(peerId: string, slotIndex: PlaycastGamepadId): void {
    const peerSlots = this.slotAssignments.get(peerId);
    if (!peerSlots) return;

    const idx = peerSlots.indexOf(slotIndex);
    if (idx !== -1) {
      peerSlots.splice(idx, 1);
    }
  }

  private resolveSlot(peerId: string, deviceIndex: number): number {
    const peerSlots = this.slotAssignments.get(peerId) ?? [];

    if (deviceIndex < peerSlots.length) {
      return peerSlots[deviceIndex];
    }

    const newSlot = this.autoAssignSlot(peerId);
    return newSlot;
  }

  private autoAssignSlot(peerId: string): number {
    const config = this.deps.gamepadConfigStore.currentState;
    if (!config.autoAssign) return -1;

    const allUsedSlots = this.getAllUsedSlots();
    if (allUsedSlots.size >= config.maxAssignments) return -1;

    for (let slot = 0; slot < MAX_GAMEPAD_SLOTS; slot++) {
      if (!allUsedSlots.has(slot) || this.isSlotShared(slot, config)) {
        const peerSlots = this.slotAssignments.get(peerId) ?? [];

        if (!config.shareFlags[slot]) {
          this.removeSlotFromOtherPeers(slot, peerId);
        }

        peerSlots.push(slot as PlaycastGamepadId);
        this.slotAssignments.set(peerId, peerSlots);
        return slot;
      }
    }

    return -1;
  }

  private getAllUsedSlots(): Set<number> {
    const used = new Set<number>();
    for (const slots of this.slotAssignments.values()) {
      for (const slot of slots) {
        used.add(slot);
      }
    }
    return used;
  }

  private isSlotShared(slot: number, config: GamepadConfigState): boolean {
    return slot < config.shareFlags.length && config.shareFlags[slot];
  }

  private removeSlotFromOtherPeers(slot: number, excludePeerId: string): void {
    for (const [peerId, slots] of this.slotAssignments.entries()) {
      if (peerId === excludePeerId) continue;
      const idx = slots.indexOf(slot as PlaycastGamepadId);
      if (idx !== -1) {
        slots.splice(idx, 1);
      }
    }
  }

  private applyButtonFiltering(xInput: XInput): XInput {
    if (!this.deps.inputRulesStore.currentState.enableGamepadFiltering) return xInput;
    return { ...xInput, wButtons: xInput.wButtons & ~FILTER_MASK };
  }
}
